import { useTheme } from "@/components/theme/themeProvider"
import { Toaster } from "@/components/ui/baseUi/sonner"
import { useCrossTabAuthSync } from "@/modules/auth/hooks/useCrossTabAuthSync"
import SplashScreen from "@/modules/ui/components/splashScreen"
import { Config } from "@modules/config"
import { reconcileSession } from "@/modules/auth/utils/reconcileSession"
import { useServicesContext } from "@modules/core/services/services.context"
import { useAutoRefreshOnStale } from "@modules/hooks/useAutoRefreshOnStale"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { WebNavigation } from "@modules/navigation/navigation"
import { AppShutdownModal } from "@modules/ui/components/popup/appShutdownModal"
import { HUConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { LoggerType } from "@modules/utils/loggerUtils"
import { Amplify } from "aws-amplify"
import { Ban, Check, CircleAlert, Info } from "lucide-react"
import { useEffect, useEffectEvent, useState } from "react"

export function App() {
  useLanguage()
  useAutoRefreshOnStale()
  useCrossTabAuthSync()
  const [displaySplashScreen, setDisplaySplashScreen] = useState<boolean>(false)
  const [init, setInit] = useState(false)
  const { theme } = useTheme()
  const { serviceManager } = useServicesContext()

  const initApp = useEffectEvent(async () => {
    setInit(false)
    console.log(LoggerType.Config + `use ${JSON.stringify(Config, null, "\t")}`)
    await serviceManager.init()
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: Config.COGNITO_USER_POOL_ID,
          userPoolClientId: Config.COGNITO_CLIENT_ID,
        },
      },
    })
    await reconcileSession()
    setInit(true)
  })

  useEffect(() => {
    if (init) return
    initApp().catch((e: unknown) => {
      console.error(LoggerType.Error + "[Boot] init app", e)
    })
    const preloadErrorHandler = () => {
      window.location.reload()
    }
    window.addEventListener("vite:preloadError", preloadErrorHandler)
    return () => {
      window.removeEventListener("vite:preloadError", preloadErrorHandler)
    }
  }, [init])

  const handleDisplaySplashScreen = useEffectEvent(() => {
    setDisplaySplashScreen(true)
    setTimeout(() => {
      setDisplaySplashScreen(false)
    }, 1500)
  })

  useEffect(() => {
    handleDisplaySplashScreen()
  }, [theme])

  if (!init) return null

  return (
    <HUConfirmPopup>
      {displaySplashScreen && <SplashScreen />}
      <AppShutdownModal />
      <Toaster
        icons={{
          success: <Check color="currentColor" />,
          error: <Ban color="currentColor" />,
          warning: <CircleAlert color="currentColor" />,
          info: <Info color="currentColor" />,
        }}
      />
      <WebNavigation />
    </HUConfirmPopup>
  )
}
